/**
 * The WoW bar (B3 · Halo + WoW bar) — the in-raid action bar and menu strip.
 *
 * Two rows, centred on the bottom edge. Row one is the six ability slots on 1-6 and three
 * quick slots, flanked by the vitals on the left. Row two is the menu strip — one icon,
 * label and key cap per panel, lit mint when that panel is up.
 *
 * Phase 2b hangs three more instruments off the same column, all of them measured to the
 * width of the slot row: the resource pool above it (rage red, mana blue), the local cast
 * bar below it, and the auto-attack lamp at the end of the row.
 *
 * The markup lives in index.html (so `need()` still catches a missing node at boot) and
 * this class only ever writes into it. Nothing here allocates per frame: every cell caches
 * the last string and class it wrote, and a static frame touches no DOM at all.
 *
 * One thing it deliberately does NOT own: casting an ability. The keys 1-6 are input.ts's,
 * and a click on a cell goes through the same host hook, so there is one path to a cast
 * and not two.
 */

import { ITEMS, ItemLocation, LEVEL_CAP, ResourceKind, xpForLevel } from '@stillsun/shared';
import type { AbilityDef, ItemStack } from '@stillsun/shared';
import { abilityIcon, icon, itemKindIcon, type IconName } from './icons.js';

/** Keyboard labels per ability slot. 1-6, on the number row and the numpad — see input.ts. */
const ABILITY_KEYS = ['1', '2', '3', '4', '5', '6'] as const;
/**
 * Quick slots have no key of their own any more: 1-6 belong to the abilities in the pivot
 * build, so a consumable is used by clicking it or from the bag.
 */
const QUICK_KEYS = ['', '', ''] as const;

/** Every panel the menu strip can reach. `map` is ui/zonemap.ts, on M. */
export type MenuId =
  'bag' | 'quests' | 'map' | 'calling' | 'scores' | 'chat' | 'feedback' | 'settings';

interface MenuDef {
  id: MenuId;
  icon: IconName;
  label: string;
  /** What the key cap says. B is the bag key; I is still an alias, and always will be. */
  cap: string;
  /** `ev.code` this item answers to, when the strip owns the binding. '' when input.ts does. */
  code: string;
  /** Drawn disabled, with a reason. */
  soon?: string;
}

/**
 * The strip, left to right. The cap is the promise; `code` is only set for the three keys
 * input.ts does not already bind (J, K, M) — everything else is bound there and the cap
 * here is a label rather than a second listener racing the first.
 */
const MENU: readonly MenuDef[] = [
  { id: 'bag', icon: 'satchel', label: 'Bag', cap: 'B', code: '' },
  { id: 'quests', icon: 'contracts', label: 'Quests', cap: 'J', code: 'KeyJ' },
  { id: 'map', icon: 'map', label: 'Map', cap: 'M', code: 'KeyM' },
  { id: 'calling', icon: 'calling', label: 'Calling', cap: 'K', code: 'KeyK' },
  // TAB cycles targets now (plan §2a), so the scoreboard moved to its own key. O is the
  // genre's convention for the who-is-here panel and nothing else claims it.
  { id: 'scores', icon: 'xp', label: 'Scores', cap: 'O', code: 'KeyO' },
  { id: 'chat', icon: 'chat', label: 'Chat', cap: 'ENTER', code: '' },
  { id: 'feedback', icon: 'feedback', label: 'Feedback', cap: 'F8', code: '' },
  { id: 'settings', icon: 'settings', label: 'Settings', cap: 'L', code: 'KeyL' },
];

/**
 * One frame's worth of one ability cell. Handed in as a single reused object so the frame
 * loop can fill it six times and allocate nothing; the bar never keeps a reference.
 */
export interface AbilitySlotView {
  /** 1 just after a cast, 0 when ready. The cooldown wash. */
  fraction: number;
  /** Milliseconds left on this slot's own cooldown — the seconds readout. */
  remainingMs: number;
  /** 1 at the moment of the press, 0 when the global cooldown is over. */
  gcdFraction: number;
  def: AbilityDef | undefined;
  /** `AbilityTracker.ready()`: cooldown, global cooldown and the rage or mana price. */
  ready: boolean;
  /** Cannot pay the ability's rage or mana cost. */
  short: boolean;
  /** Predicted stamina, which still gates the two ultimates. */
  stamina: number;
}

/** What the bar needs from its host. Everything here already exists in main.ts. */
export interface RaidBarHost {
  /** Quick slot 1/2/3 — clicked, since the number keys are the ability bar's now. */
  onQuickSlot: (index: number) => void;
  /**
   * Ability 1-6, clicked rather than keyed. The same verb the number keys fire, so there is
   * one path to a cast and not two — the host presses the button bit, exactly as input.ts does.
   */
  onAbilitySlot: (slot: number) => void;
  /** Bag (B, and I). */
  onBag: () => void;
  /** Scoreboard. The strip's button toggles it, and so does O. */
  onScores: () => void;
  onChat: () => void;
  onFeedback: () => void;
  /** ESC — closes the topmost panel. */
  onSettings: () => void;
  /** The zone map (M) — ui/zonemap.ts. The host owns the panel; this only asks for it. */
  onMap: () => void;
  /** False in the hub, on the death screen, under the kill cam and while chat is open. */
  isActive: () => boolean;
}

interface Slot {
  root: HTMLButtonElement;
  key: HTMLSpanElement;
  glyph: HTMLSpanElement;
  num: HTMLSpanElement;
  sweep: HTMLSpanElement;
  /** The global-cooldown bar along the bottom edge. Ability cells only. */
  gcd: HTMLSpanElement | null;
  /** Last written values, so a static frame touches no styles at all. */
  lastCd: number;
  lastGcd: number;
  lastNum: string;
  lastCls: string;
  lastGlyph: string;
}

interface MenuCell {
  def: MenuDef;
  root: HTMLButtonElement;
  label: HTMLSpanElement;
  on: boolean;
}

function need<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`[stillsun] missing #${id} in index.html`);
  return el as T;
}

/**
 * Fraction through the current level, 0..1 — the one place raidbar.ts touches the curve,
 * and it only ever calls `xpForLevel`. Mirrors hub/common.tsx's `levelProgress` (the hub
 * bar and this bar read the same shared curve, never a second copy of it).
 */
function xpFraction(xp: number, level: number): number {
  if (level >= LEVEL_CAP) return 1;
  const base = xpForLevel(level);
  const span = xpForLevel(level + 1) - base;
  if (span <= 0) return 1;
  return Math.max(0, Math.min(1, (xp - base) / span));
}

export class RaidBar {
  private readonly root = need<HTMLDivElement>('raidbar');
  private readonly menuRoot = need<HTMLDivElement>('rbmenu');
  private readonly menuBtn = need<HTMLButtonElement>('rbmenubtn');
  private readonly callingCard = need<HTMLDivElement>('callingcard');
  private readonly callingRows = need<HTMLDivElement>('callingrows');
  private readonly slotsRoot = need<HTMLDivElement>('rbslots');

  private readonly xpRoot = need<HTMLDivElement>('rbxp');
  private readonly xpLvl = need<HTMLSpanElement>('rbxplvl');
  private readonly xpTrack = need<HTMLDivElement>('rbxptrack');
  private readonly xpFill = need<HTMLDivElement>('rbxpfill');
  private readonly xpSweep = need<HTMLDivElement>('rbxpsweep');
  private readonly xpLabel = need<HTMLSpanElement>('rbxplabel');

  // Phase 2b: the resource pool, the local cast bar and the auto-attack lamp.
  private readonly resRoot = need<HTMLDivElement>('rbres');
  private readonly resFill = need<HTMLDivElement>('rbresfill');
  private readonly resNum = need<HTMLSpanElement>('rbresnum');
  private readonly castRoot = need<HTMLDivElement>('rbcast');
  private readonly castFill = need<HTMLDivElement>('rbcastfill');
  private readonly castName = need<HTMLSpanElement>('rbcastname');
  private readonly castTime = need<HTMLSpanElement>('rbcasttime');
  private readonly autoEl = need<HTMLDivElement>('rbauto');

  private readonly abilities: Slot[];
  private readonly quick: Slot[];
  private readonly menu: MenuCell[] = [];

  private readonly host: RaidBarHost;
  /** ui/quests.ts's panel. The strip toggles a `hidden` class on it and never its `on`
   * class, which quests.ts owns and re-adds on every S2C.QuestState. */
  private readonly questPanel: HTMLElement | null;

  private callingKey = '';
  private lastQuickKey = '';
  private visible = false;
  private questsHidden = false;
  private callingOpen = false;
  private sheetOpen = false;

  /** -1 until the first Progress arrives, so the opening paint never sweeps from 0. */
  private lastXpFrac = -1;
  private lastXpLevel = -1;

  // Latches for everything Phase 2b paints every frame. A frame where nothing moved has to
  // touch no DOM at all, which is the whole contract of this file.
  private resKind: ResourceKind = ResourceKind.None;
  private lastResFrac = -1;
  private lastResNum = '';
  private castOn = false;
  private lastCastFrac = -1;
  private lastCastName = '';
  private lastCastTime = '';
  private autoOn = false;

  constructor(host: RaidBarHost) {
    this.host = host;
    this.questPanel = document.getElementById('questpanel');

    this.abilities = [0, 1, 2, 3, 4, 5].map((i) => {
      const slot = this.dressSlot(
        need<HTMLButtonElement>(`ab${String(i + 1)}`),
        ABILITY_KEYS[i] ?? '',
        true,
      );
      // Click-to-cast. The cursor is always the player's (plan §1c), so a cell is a button;
      // it goes through the host, which presses the same button bit the key does.
      slot.root.addEventListener('pointerdown', (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        host.onAbilitySlot(i);
      });
      return slot;
    });
    this.quick = [0, 1, 2].map((i) => {
      const slot = this.dressSlot(
        need<HTMLButtonElement>(`qs${String(i + 1)}`),
        QUICK_KEYS[i] ?? '',
      );
      // The cursor is always the player's now (plan §1c), so a click on a cell is the
      // ordinary way to use one.
      slot.root.addEventListener('pointerdown', (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        host.onQuickSlot(i);
      });
      return slot;
    });

    this.buildMenu();
    this.bindKeys();
    this.watchXpWidth();
  }

  // ------------------------------------------------------------------ building

  /**
   * Fills one square slot with its key cap, glyph, count and cooldown wash — and, for an
   * ability cell, the global-cooldown bar that runs along its bottom edge.
   *
   * The GCD is drawn on a different axis from the cooldown on purpose: the cooldown wash
   * climbs the cell vertically in volt, and a global cooldown wearing the same paint would
   * read as "all six of these just went on cooldown", which is exactly what it is not.
   */
  private dressSlot(root: HTMLButtonElement, keyLabel: string, ability = false): Slot {
    root.textContent = '';
    const key = document.createElement('span');
    key.className = 'rbk';
    key.textContent = keyLabel;
    const glyph = document.createElement('span');
    glyph.className = 'rbg';
    const num = document.createElement('span');
    num.className = 'rbn';
    const sweep = document.createElement('span');
    sweep.className = 'rbcd';
    let gcd: HTMLSpanElement | null = null;
    if (ability) {
      gcd = document.createElement('span');
      gcd.className = 'rbgcd';
    }
    root.append(sweep, glyph, key, num);
    if (gcd) root.append(gcd);
    return {
      root,
      key,
      glyph,
      num,
      sweep,
      gcd,
      lastCd: -1,
      lastGcd: -1,
      lastNum: '',
      lastCls: '',
      lastGlyph: '',
    };
  }

  /**
   * Keeps `--xpw` and `--xpx` on #rbxp equal to #rbslots' rendered width and left offset,
   * so the XP row is "exactly the width of the slot row" — and sits under it — on every
   * layout: desktop's flanked row (where the health flank on the left has no match on the
   * right, so #rbslots is not itself centred in the bar and a merely-centred row would
   * float next to it) and the narrower strip once the 1080px breakpoint drops the flank.
   * Nothing here hard-codes either breakpoint. jsdom (unit tests) has no ResizeObserver, so
   * this is a no-op there and the CSS fallback holds.
   */
  private watchXpWidth(): void {
    const sync = (): void => {
      const bar = this.root.getBoundingClientRect();
      const slots = this.slotsRoot.getBoundingClientRect();
      if (slots.width <= 0) return;
      const w = `${String(slots.width)}px`;
      const x = `${String(slots.left - bar.left)}px`;
      // The resource bar above the slots and the cast bar below them hang off the same two
      // numbers as the XP row: four things stacked in one column, measured once.
      for (const el of [this.xpRoot, this.resRoot, this.castRoot]) {
        el.style.setProperty('--xpw', w);
        el.style.setProperty('--xpx', x);
      }
    };
    // #rbslots resizes whenever its own contents change — ResizeObserver catches that. It
    // does NOT resize when the 1080px breakpoint drops the health flank beside it (that is
    // #rbslots' sibling, not its child): that only shifts #rbslots' own *offset* inside
    // #rbrow, so a plain resize listener covers it.
    if (typeof ResizeObserver !== 'undefined') new ResizeObserver(sync).observe(this.slotsRoot);
    window.addEventListener('resize', sync);
    sync();
  }

  private buildMenu(): void {
    for (const def of MENU) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = def.soon === undefined ? 'rbm' : 'rbm off';
      btn.title =
        def.soon === undefined ? `${def.label} · ${def.cap}` : `${def.label} — ${def.soon}`;
      const ic = document.createElement('span');
      ic.className = 'rbmi';
      ic.innerHTML = icon(def.icon, 22);
      const label = document.createElement('span');
      label.className = 'rbml';
      label.textContent = def.label;
      const cap = document.createElement('span');
      cap.className = 'rbmk';
      cap.textContent = def.cap;
      btn.append(ic, label, cap);
      btn.addEventListener('pointerdown', (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        this.activate(def.id);
      });
      this.menuRoot.append(btn);
      this.menu.push({ def, root: btn, label, on: false });
    }

    this.menuBtn.textContent = 'Menu';
    this.menuBtn.addEventListener('pointerdown', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      this.setSheet(!this.sheetOpen);
    });
  }

  /**
   * The phone sheet. `body.menusheet` is what hides the halo behind it — a class on #halo
   * itself would be overwritten by the state machine on the next frame.
   */
  private setSheet(open: boolean): void {
    this.sheetOpen = open;
    this.root.classList.toggle('sheet', open);
    this.menuBtn.classList.toggle('on', open);
    document.body.classList.toggle('menusheet', open);
  }

  /**
   * J / K / M. input.ts owns every other binding on the strip, so this listener only ever
   * claims the three keys nothing else answers to — and it copies input.ts's guards
   * exactly: no repeats, no modifiers, nothing while a text field has the focus, and
   * nothing at all unless the raid HUD is live.
   */
  private bindKeys(): void {
    window.addEventListener('keydown', (ev) => {
      if (ev.repeat || ev.ctrlKey || ev.altKey || ev.metaKey) return;
      const target = ev.target;
      if (target instanceof HTMLElement) {
        if (
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable
        ) {
          return;
        }
      }
      if (!this.host.isActive()) return;
      for (const cell of this.menu) {
        if (cell.def.code !== '' && cell.def.code === ev.code) {
          ev.preventDefault();
          this.activate(cell.def.id);
          return;
        }
      }
    });
  }

  // ------------------------------------------------------------------- actions

  private activate(id: MenuId): void {
    switch (id) {
      case 'bag':
        this.host.onBag();
        break;
      case 'quests':
        this.toggleQuests();
        break;
      case 'map':
        this.host.onMap();
        break;
      case 'calling':
        this.toggleCalling();
        break;
      case 'scores':
        this.host.onScores();
        break;
      case 'chat':
        this.host.onChat();
        break;
      case 'feedback':
        this.host.onFeedback();
        break;
      case 'settings':
        this.host.onSettings();
        break;
    }
  }

  private toggleQuests(): void {
    this.questsHidden = !this.questsHidden;
    this.questPanel?.classList.toggle('hidden', this.questsHidden);
    this.setMenuOn('quests', !this.questsHidden);
  }

  private toggleCalling(): void {
    this.setCallingOpen(!this.callingOpen);
  }

  setCallingOpen(open: boolean): void {
    this.callingOpen = open;
    this.callingCard.classList.toggle('on', open);
    this.setMenuOn('calling', open);
  }

  get callingCardOpen(): boolean {
    return this.callingOpen;
  }

  /** Lights one strip item. Called from main.ts for the panels it owns the state of. */
  setMenuOn(id: MenuId, on: boolean): void {
    for (const cell of this.menu) {
      if (cell.def.id !== id) continue;
      if (cell.on === on) return;
      cell.on = on;
      cell.root.classList.toggle('on', on);
      return;
    }
  }

  // -------------------------------------------------------------------- paint

  /** Shows or hides the whole bar (hidden outside a raid and while dead). */
  setVisible(on: boolean): void {
    if (on === this.visible) return;
    this.visible = on;
    this.root.classList.toggle('on', on);
    // The tracker is pinned, so Quests reads lit from the moment the bar comes up.
    if (on) this.setMenuOn('quests', !this.questsHidden);
    if (!on) {
      this.setCallingOpen(false);
      this.setSheet(false);
    }
  }

  /**
   * Rebuilds the ability glyphs for a Calling, and the read-only Calling card behind K.
   * `null` (no Calling chosen, or an M2 server that never sends Progress) leaves three
   * empty cells rather than a wrong set.
   */
  setCalling(defs: readonly (AbilityDef | undefined)[] | null): void {
    // Six slots since v3, and 3..5 are `undefined` until Phase 2b authors them. An unfilled
    // slot draws as an empty cell and keeps its key, which is what "the bar grows to six" has
    // to mean before there is anything to put in the last three.
    const key = defs ? defs.map((d) => d?.id ?? '-').join(',') : '';
    if (key === this.callingKey) return;
    this.callingKey = key;

    this.abilities.forEach((cell, slot) => {
      const def = defs?.[slot];
      cell.glyph.innerHTML = def ? abilityIcon(def.id, 26) : '';
      cell.key.textContent = ABILITY_KEYS[slot] ?? '';
      cell.root.title = def ? `${def.name} — ${def.desc}` : '';
      cell.root.setAttribute('aria-label', def ? def.name : `Ability ${String(slot + 1)}`);
      cell.root.classList.toggle('empty', def === undefined);
    });

    this.callingRows.textContent = '';
    if (!defs || defs.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'empty';
      empty.textContent = 'No Calling chosen. Pick one in Lanternfall before you deploy.';
      this.callingRows.append(empty);
      return;
    }
    defs.forEach((def, slot) => {
      if (!def) return;
      const row = document.createElement('div');
      row.className = 'cr';
      const k = document.createElement('span');
      k.className = 'crk';
      k.textContent = ABILITY_KEYS[slot] ?? '';
      const g = document.createElement('span');
      g.className = 'crg';
      g.innerHTML = abilityIcon(def.id, 22);
      const body = document.createElement('div');
      const n = document.createElement('div');
      n.className = 'crn';
      n.textContent = def.name;
      const d = document.createElement('div');
      d.className = 'crd';
      d.textContent = def.desc;
      body.append(n, d);
      row.append(k, g, body);
      this.callingRows.append(row);
    });
  }

  /**
   * The XP row. Driven straight off `S2C.Progress` (via AbilityTracker's mirror of it),
   * the same message the ability cooldowns and the Calling glyphs come from — so this
   * never drifts from what the halo and the hub header already show.
   *
   * `leveledUp` suppresses the gain sweep (the fill just dropped back to the new level's
   * own progress, which is not "newly filled span") and flashes the track instead.
   */
  setProgress(xp: number, level: number, leveledUp: boolean): void {
    const frac = xpFraction(xp, level);
    if (frac === this.lastXpFrac && level === this.lastXpLevel) return;
    const from = this.lastXpLevel < 0 ? frac : this.lastXpFrac;
    this.lastXpFrac = frac;
    this.lastXpLevel = level;

    this.xpFill.style.transform = `scaleX(${String(frac)})`;
    this.xpLvl.textContent = String(level);
    this.xpLabel.textContent =
      level >= LEVEL_CAP
        ? `LEVEL ${String(level)} · CAP`
        : `LEVEL ${String(level)} · ${String(Math.max(0, xpForLevel(level + 1) - xp))} XP TO ${String(level + 1)}`;

    if (leveledUp) {
      this.restartAnim(this.xpTrack, 'flash');
    } else if (frac > from) {
      this.xpSweep.style.setProperty('--sweep-from', `${String(from * 100)}%`);
      this.xpSweep.style.setProperty('--sweep-to', `${String(frac * 100)}%`);
      this.restartAnim(this.xpSweep, 'on');
    }
  }

  /** Restarts a one-shot CSS animation even if it is already mid-flight. */
  private restartAnim(el: HTMLElement, cls: string): void {
    el.classList.remove(cls);
    void el.offsetWidth; // force reflow
    el.classList.add(cls);
  }

  /**
   * Per-frame paint of one ability slot. `view.fraction` is 1 just after a cast and 0 when
   * ready; `remainingMs` is only used for the seconds readout, so the two never disagree.
   * Returns true on the frame the slot comes off cooldown — the halo's wake event.
   *
   * The caller hands in one reused object rather than seven positional numbers: nothing here
   * keeps a reference to it, so a frame loop can fill the same scratch six times and allocate
   * nothing. `ready` is `AbilityTracker.ready()` — cooldown, global cooldown and the rage or
   * mana price, decided in one place so the cell the bar dims is exactly the cell the server
   * would refuse.
   */
  updateAbility(slot: number, view: AbilitySlotView): boolean {
    const cell = this.abilities[slot];
    if (!cell) return false;
    const { def } = view;

    // Quantised to a percent: the wash is 56 px tall, so a finer step is invisible and
    // saves ~99 of every 100 style writes.
    const pct = Math.round(view.fraction * 100);
    const wasCooling = cell.lastCd > 0;
    if (pct !== cell.lastCd) {
      cell.lastCd = pct;
      cell.sweep.style.setProperty('--cd', `${String(pct)}%`);
    }

    // The global cooldown, on every cell at once. An ability that ignores it (`gcd: false`,
    // the documented read) never draws one — a defensive that is up has to look up.
    if (cell.gcd) {
      const onGcd = def !== undefined && def.gcd !== false;
      const gpct = onGcd ? Math.round(view.gcdFraction * 100) : 0;
      if (gpct !== cell.lastGcd) {
        cell.lastGcd = gpct;
        cell.gcd.style.setProperty('--gcd', `${String(gpct)}%`);
      }
    }

    const secs = view.remainingMs > 0 ? Math.ceil(view.remainingMs / 1000) : 0;
    const label = secs > 0 ? String(secs) : '';
    if (label !== cell.lastNum) {
      cell.lastNum = label;
      cell.num.textContent = label;
    }

    const noStam = def !== undefined && def.staminaCost > 0 && view.stamina < def.staminaCost;
    const short = view.short;
    const ready = view.ready && !noStam;
    const cls = `${ready ? 'r' : ''}|${noStam ? 's' : ''}|${short ? 'p' : ''}|${secs > 0 ? 'c' : ''}`;
    if (cls !== cell.lastCls) {
      cell.lastCls = cls;
      cell.root.classList.toggle('ready', ready);
      cell.root.classList.toggle('nostam', noStam);
      cell.root.classList.toggle('nores', short);
      cell.root.classList.toggle('cool', secs > 0);
    }
    return wasCooling && pct === 0 && def !== undefined;
  }

  /**
   * The resource pool: rage for a Vanguard, mana for a Warden, nothing at all for a Calling
   * that has neither (`ResourceKind.None` hides the bar rather than drawing an empty one).
   *
   * Red and blue, and not the raid palette's four voices, because this is the one place in the
   * HUD where the colour *is* the rule the genre already taught the player: red fills as you
   * fight, blue drains as you spend.
   */
  setResource(kind: ResourceKind, value: number, max: number): void {
    if (kind !== this.resKind) {
      this.resKind = kind;
      this.resRoot.classList.toggle('on', kind !== ResourceKind.None);
      this.resRoot.classList.toggle('mana', kind === ResourceKind.Mana);
    }
    if (kind === ResourceKind.None || max <= 0) return;
    const frac = Math.max(0, Math.min(1, value / max));
    const rounded = Math.round(frac * 200) / 200;
    if (rounded !== this.lastResFrac) {
      this.lastResFrac = rounded;
      this.resFill.style.transform = `scaleX(${rounded.toFixed(3)})`;
    }
    const num = `${String(Math.round(value))} / ${String(Math.round(max))}`;
    if (num !== this.lastResNum) {
      this.lastResNum = num;
      this.resNum.textContent = num;
    }
  }

  /**
   * The local cast bar. `castMs` is what is **left** (v3.1 §9.1) and `fraction` how far the
   * bar has filled, so a 0 clears the bar on the frame it arrives — an interrupt has to take
   * the bar away instantly or the player learns to distrust it.
   */
  setCast(def: AbilityDef | undefined, castMs: number, fraction: number): void {
    const on = castMs > 0 && def !== undefined;
    if (on !== this.castOn) {
      this.castOn = on;
      this.castRoot.classList.toggle('on', on);
    }
    if (!on || !def) return;
    const rounded = Math.round(Math.max(0, Math.min(1, fraction)) * 200) / 200;
    if (rounded !== this.lastCastFrac) {
      this.lastCastFrac = rounded;
      this.castFill.style.transform = `scaleX(${rounded.toFixed(3)})`;
    }
    if (def.name !== this.lastCastName) {
      this.lastCastName = def.name;
      this.castName.textContent = def.name;
    }
    const time = `${(castMs / 1000).toFixed(1)}s`;
    if (time !== this.lastCastTime) {
      this.lastCastTime = time;
      this.castTime.textContent = time;
    }
  }

  /**
   * The auto-attack lamp. `Buttons.AutoAttack` is a rising-edge toggle the **server** owns, so
   * this is a report and never a command: it lights from the local press the client already
   * sent and is never used to decide anything.
   */
  setAutoAttack(on: boolean): void {
    if (on === this.autoOn) return;
    this.autoOn = on;
    this.autoEl.classList.toggle('on', on);
  }

  /** The three quick slots, straight off the last S2C.Inventory. */
  setQuickSlots(items: readonly ItemStack[]): void {
    let key = '';
    for (let i = 0; i < 3; i++) {
      const stack = items.find((s) => s.location === ItemLocation.Quick && s.slot === i);
      key += `${stack ? `${stack.itemId}x${String(stack.qty)}` : '-'}|`;
    }
    if (key === this.lastQuickKey) return;
    this.lastQuickKey = key;

    for (let i = 0; i < 3; i++) {
      const cell = this.quick[i];
      if (!cell) continue;
      const stack = items.find((s) => s.location === ItemLocation.Quick && s.slot === i);
      const def = stack ? ITEMS[stack.itemId] : undefined;
      const glyph = stack ? itemKindIcon(def?.kind) : '';
      if (glyph !== cell.lastGlyph) {
        cell.lastGlyph = glyph;
        cell.glyph.innerHTML = glyph === '' ? '' : icon(glyph, 24);
      }
      const num = stack && stack.qty > 1 ? String(stack.qty) : '';
      if (num !== cell.lastNum) {
        cell.lastNum = num;
        cell.num.textContent = num;
      }
      cell.root.classList.toggle('empty', !stack);
      cell.root.classList.toggle('filled', !!stack);
      cell.root.title = def ? `${def.name} — ${def.desc}` : 'empty';
    }
  }

  /** Leaving the raid. */
  clear(): void {
    this.setVisible(false);
    this.setCalling(null);
    // A cast bar and an auto-attack lamp left up over the hub would be two lies at once.
    this.setCast(undefined, 0, 0);
    this.setAutoAttack(false);
    this.setResource(ResourceKind.None, 0, 0);
    this.questsHidden = false;
    this.questPanel?.classList.remove('hidden');
    for (const cell of this.menu) {
      cell.on = false;
      cell.root.classList.remove('on');
    }
  }
}
